import React, { useEffect, useState, useMemo } from "react";
import api from "@/lib/api";
import { Search, ExternalLink } from "lucide-react";

export default function JurisdictionsAdmin() {
    const [rows, setRows] = useState([]);
    const [q, setQ] = useState("");
    const [selected, setSelected] = useState(null);

    useEffect(() => {
        (async () => {
            const { data } = await api.get(`/jurisdictions?limit=200`);
            setRows(data.items || []);
        })();
    }, []);

    const filtered = useMemo(() => {
        if (!q) return rows;
        const s = q.toLowerCase();
        return rows.filter((r) =>
            (r.city || "").toLowerCase().includes(s) ||
            (r.state || "").toLowerCase().includes(s) ||
            (r.county || "").toLowerCase().includes(s) ||
            (r.authority_name || "").toLowerCase().includes(s)
        );
    }, [rows, q]);

    return (
        <div className="p-6 md:p-10" data-testid="page-jurisdictions">
            <div>
                <div className="chip chip-navy">Reference data</div>
                <h1 className="mt-3 font-display text-4xl tracking-tight leading-none italic">Jurisdictions</h1>
                <div className="mt-2 text-sm text-muted-foreground">
                    Building department reference for permit workflows. {rows.length} jurisdictions on file.
                </div>
            </div>

            <div className="mt-6 relative max-w-md">
                <Search size={12} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search city, state, county, authority…" className="w-full border border-border bg-background pl-8 pr-3 py-2 text-sm" data-testid="juris-search" />
            </div>

            <div className="mt-6 grid md:grid-cols-3 gap-6">
                <div className="md:col-span-1 border border-border max-h-[600px] overflow-y-auto" data-testid="juris-list">
                    {filtered.map((j) => (
                        <button key={j.id} onClick={() => setSelected(j)} data-testid={`juris-row-${j.id}`}
                            className={`w-full text-left px-4 py-3 border-b border-border last:border-b-0 hover:bg-secondary/40 ${selected?.id === j.id ? "bg-secondary" : ""}`}>
                            <div className="text-sm font-medium">{j.city}, {j.state}</div>
                            <div className="text-[11px] text-muted-foreground truncate">{j.authority_name}</div>
                        </button>
                    ))}
                    {filtered.length === 0 && <div className="p-4 text-sm text-muted-foreground">No matches.</div>}
                </div>
                <div className="md:col-span-2">
                    {selected ? <JurisDetail j={selected} /> : (
                        <div className="border border-border p-10 text-center text-sm text-muted-foreground">Pick a jurisdiction to see details.</div>
                    )}
                </div>
            </div>

            <div className="mt-6 border border-amber-500/40 bg-amber-500/5 p-4 text-xs" data-testid="juris-disclaimer">
                <b>Important:</b> Independently verify all requirements with the Authority Having Jurisdiction.
                Data is compiled from public building department websites and may not reflect the most recent
                code adoptions, fee schedules, or process changes.
            </div>
        </div>
    );
}

function JurisDetail({ j }) {
    return (
        <div className="border border-border p-6" data-testid="juris-detail">
            <div className="overline">Authority</div>
            <h2 className="font-display text-2xl italic mt-1">{j.authority_name}</h2>
            <div className="mt-1 text-sm text-muted-foreground">{j.city}, {j.county} County, {j.state}</div>

            <div className="mt-6 grid md:grid-cols-2 gap-5 text-xs">
                <Section title="Contact">
                    {j.building_department && <a href={j.building_department} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 underline"><ExternalLink size={11} /> Building department</a>}
                    <ul className="mt-2 space-y-1">
                        {(j.contacts || []).map((c, i) => <li key={i}>{c.role}: {c.phone}</li>)}
                    </ul>
                </Section>
                <Section title="Filing">
                    <ul className="space-y-1">
                        {(j.filing_urls || []).map((u, i) => <li key={i}><a href={u} target="_blank" rel="noreferrer" className="underline break-all">{u}</a></li>)}
                    </ul>
                </Section>
                <Section title="Permit types">
                    <div className="flex flex-wrap gap-1">
                        {(j.permit_types || []).map((p) => <span key={p} className="border border-border px-2 py-0.5 text-[10px]">{p}</span>)}
                    </div>
                </Section>
                <Section title="Inspection types">
                    <div className="flex flex-wrap gap-1">
                        {(j.inspection_types || []).map((p) => <span key={p} className="border border-border px-2 py-0.5 text-[10px]">{p}</span>)}
                    </div>
                </Section>
                <Section title="Code editions">
                    {(j.code_editions || []).map((c, i) => <div key={i}>{c}</div>)}
                </Section>
                <Section title="Processing / expiration">
                    <div>Est. processing days: <b>{j.estimated_processing_days || "—"}</b></div>
                    <div className="mt-1">{j.expiration_rules || "—"}</div>
                </Section>
            </div>

            <div className="mt-6 text-[10px] text-muted-foreground">
                Source: {j.source} · Last verified: {j.last_verified_date}
            </div>
        </div>
    );
}

function Section({ title, children }) {
    return <div><div className="overline mb-1">{title}</div>{children}</div>;
}
